/**
 * @fileoverview Project Info Header Component
 *
 * Displays and allows editing of:
 * - Project Name & Address (inline edit together)
 * - Project Managers (multi-select dropdown, inline edit with name/address)
 * - Client Info Card (clickable or attach)
 * - Timeline Card (editable)
 * - Budget Card (editable)
 *
 * Features:
 * - Inline editing for name, address, and project managers
 * - Multi-select dropdown for project managers
 * - Individual card editing for timeline and budget
 * - Client attachment/viewing
 *
 * @version 1.0.0
 */

import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FaUser,
  FaCalendarAlt,
  FaDollarSign,
  FaEnvelope,
  FaPhone,
  FaPencilAlt,
  FaCheck,
  FaTimes,
  FaExternalLinkAlt,
  FaPlus,
  FaUserTie,
  FaHome,
  FaBuilding,
  FaHammer,
} from "react-icons/fa";
import { formatDateET } from "../../../../utils/dateUtils";
import "./ProjectInfoHeader.css";
import AttachClientModal from "../../../../components/admin/AttachClientModal";
import ClientInfoModal from "../../../../components/admin/ClientInfoModal";

/**
 * Get project type icon based on type string
 */
const getProjectTypeIcon = (type) => {
  switch (type) {
    case "New Build":
      return <FaHammer />;
    case "Renovation":
      return <FaHome />;
    case "Addition":
      return <FaBuilding />;
    default:
      return <FaHammer />;
  }
};

/**
 * ProjectInfoHeader Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.project - Project data
 * @param {Function} props.onUpdate - Update handler
 * @param {boolean} props.isUpdating - Update in progress
 * @param {Function} props.onClientClick - Handler for client card click
 * @param {Function} props.onAttachClient - Handler to attach client
 */
const ProjectInfoHeader = ({
  projectId,
  project,
  onUpdate,
  isUpdating = false,
}) => {
  /* ============================================
     STATE MANAGEMENT
     ============================================ */

  // Name/Address/PM editing (all together)
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedAddress, setEditedAddress] = useState("");
  const [editedProjectManagerIds, setEditedProjectManagerIds] = useState([]);

  // State Client Modals
  const queryClient = useQueryClient(); // Initialize query client
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [showAttachClientModal, setShowAttachClientModal] = useState(false);

  // Timeline editing
  const [isEditingTimeline, setIsEditingTimeline] = useState(false);
  const [editedStartDate, setEditedStartDate] = useState("");
  const [editedEstCompletion, setEditedEstCompletion] = useState("");
  const [editedActualCompletion, setEditedActualCompletion] = useState("");

  const isWarranty =
    project?.projectStatus === "Construction Complete / Under Warranty";

  // Budget editing
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [editedBudget, setEditedBudget] = useState("");

  /* ============================================
     EFFECTS
     ============================================ */

  // Update local state when project changes
  useEffect(() => {
    if (project) {
      setEditedName(project.projectName || "");
      setEditedAddress(project.projectAddress || "");
      // Use projectManagerIds from the project data
      setEditedProjectManagerIds(project.projectManagerIds || []);
      setEditedStartDate(project.startDate || "");
      setEditedEstCompletion(project.estimatedCompletion || "");
      setEditedActualCompletion(project.actualCompletion || "");
      setEditedBudget(project.budget ? project.budget.toLocaleString() : "");
    }
  }, [project]);

  /* ============================================
     EVENT HANDLERS
     ============================================ */

  /**
   * Handle edit name/address/PM button click
   */
  const handleEditName = () => {
    setIsEditingName(true);
  };

  /**
   * Save name, address, and project managers
   */
  const handleSaveName = async () => {
    const updates = {
      projectName: editedName,
      projectAddress: editedAddress,
      projectManager: editedProjectManagerIds, // Array of Team record IDs
    };

    await onUpdate(updates);
    setIsEditingName(false);
  };

  /**
   * Handle edit timeline button
   */
  const handleEditTimeline = () => {
    setIsEditingTimeline(true);
  };

  /**
   * Save timeline
   */
  const handleSaveTimeline = async () => {
    const updates = {
      startDate: editedStartDate,
      estimatedCompletion: editedEstCompletion,
    };

    if (isWarranty) {
      updates.actualCompletion = editedActualCompletion;
    }

    await onUpdate(updates);
    setIsEditingTimeline(false);
  };

  /**
   * Handle edit budget button
   */
  const handleEditBudget = () => {
    setIsEditingBudget(true);
  };

  /**
   * Save budget
   */
  const handleSaveBudget = async () => {
    const updates = {
      budget: Number(editedBudget.replace(/,/g, "")),
    };

    await onUpdate(updates);
    setIsEditingBudget(false);
  };

  /**
   * Handle client click - navigate to client details or show attach modal
   */
  const handleClientClick = () => {
    if (project?.client?.id) {
      // Client exists - navigate to client details
      setShowClientInfoModal(true);
    } else {
      // No client - open attach modal
      setShowAttachClientModal(true);
    }
  };

  /**
   * Handle successful client attachment
   * Invalidates queries to refresh project data
   */
  const handleClientAttached = () => {
    queryClient.invalidateQueries({ queryKey: ["project", projectId] });
  };

  /**
   * Toggle project manager selection (multi-select)
   */
  const toggleProjectManager = (managerId) => {
    setEditedProjectManagerIds((prev) => {
      if (prev.includes(managerId)) {
        return prev.filter((id) => id !== managerId);
      } else {
        return [...prev, managerId];
      }
    });
  };

  /* ============================================
     RENDER
     ============================================ */

  return (
    <div className="apd-section">
      <div className="apd-header-badge">
        <div className="apd-type-badge">
          {getProjectTypeIcon(project?.projectType)}
          <span>{project?.projectType}</span>
        </div>
        <button
          className="apd-btn-edit-inline"
          onClick={handleEditName}
          data-tooltip="Edit name, address & managers"
        >
          <FaPencilAlt />
        </button>
      </div>
      <div className="pih-header-wrapper">
        {/* Header Top: Name, Address, Project Managers */}
        <div className="apd-header-top">
          {isEditingName ? (
            <div className="apd-inline-edit-name">
              <span data-tooltip="Project Name">
                <input
                  type="text"
                  className="apd-input apd-input-name"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Project Name"
                  autoFocus
                />
              </span>
              <span data-tooltip="Project Address">
                <input
                  type="text"
                  className="apd-input apd-input-address"
                  value={editedAddress}
                  onChange={(e) => setEditedAddress(e.target.value)}
                  placeholder="Project Address"
                />
              </span>

              {/* Project Managers Multi-Select */}
              <div className="pih-pm-select-wrapper">
                <label className="pih-pm-label">
                  <FaUserTie /> Assign Project Manager(s)
                </label>
                <div className="pih-pm-select">
                  {!project?.availableProjectManagers ||
                  project.availableProjectManagers.length === 0 ? (
                    <div className="pih-pm-empty">
                      No project managers found
                    </div>
                  ) : (
                    project.availableProjectManagers.map((member) => (
                      <label
                        key={member.id}
                        className="checkbox-label"
                        data-tooltip-bottom="Assign PM"
                      >
                        <input
                          type="checkbox"
                          checked={editedProjectManagerIds.includes(member.id)}
                          onChange={() => toggleProjectManager(member.id)}
                        />
                        <span>{member.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="apd-inline-edit-actions">
                <button
                  className="apd-save-btn"
                  onClick={handleSaveName}
                  disabled={isUpdating}
                >
                  <FaCheck /> Save
                </button>
                <button
                  className="apd-cancel-btn"
                  onClick={() => setIsEditingName(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="apd-project-name">{project?.projectName}</h1>
            </>
          )}
        </div>

        {/* Address and Project Managers (read-only view) */}
        {!isEditingName && (
          <>
            <div className="pih-info-wrapper">
              <p className="pih-project-address">{project?.projectAddress}</p>
              <div
                className="pih-pm-wrapper"
                data-tooltip-bottom="Project Manager(s)"
              >
                <p className="pih-project-manager">
                  <label>
                    <FaUserTie />
                  </label>
                </p>
                <p>
                  {project.projectManager || "Project Manager Not Assigned"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Info Cards Grid */}
      <div className="apd-info-grid">
        {/* Client Info Card */}
        <div
          className={`apd-info-card ${
            project?.client?.id
              ? "apd-info-card-clickable"
              : "apd-info-card-attach"
          }`}
          onClick={handleClientClick}
          data-tooltip={
            project?.client?.id
              ? "View client details"
              : "Attach client to this project"
          }
        >
          <h3>
            <FaUser /> Client
            {project?.client?.id && (
              <FaExternalLinkAlt className="apd-info-link-icon" />
            )}
          </h3>

          {project?.client?.id ? (
            <>
              <p className="apd-info-primary">{project.client.name}</p>
              <p className="apd-info-secondary">
                <FaEnvelope /> {project.client.email}
              </p>
              <p className="apd-info-secondary">
                <FaPhone /> {project.client.phone}
              </p>
            </>
          ) : (
            <div className="apd-attach-client-prompt">
              <FaPlus className="apd-attach-icon" />
              <span className="apd-attach-text">Attach Client Record</span>
            </div>
          )}
        </div>

        {/* Timeline Card */}
        <div className="apd-info-card">
          <h3>
            <FaCalendarAlt /> Timeline
            {!isEditingTimeline && (
              <button
                className="apd-btn-edit-inline apd-btn-edit-card"
                onClick={handleEditTimeline}
                data-tooltip="Edit timeline"
              >
                <FaPencilAlt />
              </button>
            )}
          </h3>

          {isEditingTimeline ? (
            <div className="apd-inline-edit-card">
              <div className="apd-info-row">
                <label>Start Date</label>
                <input
                  type="date"
                  className="apd-input apd-input-date"
                  value={editedStartDate}
                  onChange={(e) => setEditedStartDate(e.target.value)}
                />
              </div>
              <div className="apd-info-row">
                <label>Est. Completion</label>
                <input
                  type="date"
                  className="apd-input apd-input-date"
                  value={editedEstCompletion}
                  onChange={(e) => setEditedEstCompletion(e.target.value)}
                />
              </div>
              {isWarranty && (
                <div className="apd-info-row">
                  <label>Actual Completion</label>
                  <input
                    type="date"
                    className="apd-input apd-input-date"
                    value={editedActualCompletion}
                    onChange={(e) => setEditedActualCompletion(e.target.value)}
                  />
                </div>
              )}
              <div className="apd-info-row apd-info-row-readonly">
                <span>Selections Due</span>
                <strong>
                  {project?.selectionsDueDate
                    ? formatDateET(project.selectionsDueDate)
                    : "—"}
                </strong>
              </div>
              <div className="apd-inline-edit-actions">
                <button
                  className="apd-save-btn"
                  onClick={handleSaveTimeline}
                  disabled={isUpdating}
                >
                  <FaCheck /> Save
                </button>
                <button
                  className="apd-cancel-btn"
                  onClick={() => setIsEditingTimeline(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="apd-info-row">
                <span>Start Date</span>
                <strong>
                  {project?.startDate ? formatDateET(project.startDate) : "—"}
                </strong>
              </div>
              <div className="apd-info-row">
                <span>Est. Completion</span>
                <strong>
                  {project?.estimatedCompletion
                    ? formatDateET(project.estimatedCompletion)
                    : "—"}
                </strong>
              </div>
              {isWarranty && (
                <div className="apd-info-row">
                  <span>Actual Completion</span>
                  <strong>
                    {project?.actualCompletion
                      ? formatDateET(project.actualCompletion)
                      : "—"}
                  </strong>
                </div>
              )}
              <div className="apd-info-row">
                <span>Selections Due</span>
                <strong>
                  {project?.selectionsDueDate
                    ? formatDateET(project.selectionsDueDate)
                    : "—"}
                </strong>
              </div>
            </>
          )}
        </div>

        {/* Budget Card */}
        <div className="apd-info-card">
          <h3>
            <FaDollarSign /> Budget
            {!isEditingBudget && (
              <button
                className="apd-btn-edit-inline apd-btn-edit-card"
                onClick={handleEditBudget}
                data-tooltip="Edit budget"
              >
                <FaPencilAlt />
              </button>
            )}
          </h3>

          {isEditingBudget ? (
            <div className="apd-inline-edit-card">
              <div className="apd-budget-input-wrapper">
                <span className="apd-budget-prefix">$</span>
                <input
                  type="text"
                  className="apd-input apd-input-budget"
                  value={editedBudget}
                  onChange={(e) => {
                    // Allow only numbers and commas
                    const value = e.target.value.replace(/[^0-9,]/g, "");
                    setEditedBudget(value);
                  }}
                  placeholder="0"
                  autoFocus
                />
              </div>
              <div className="apd-inline-edit-actions">
                <button
                  className="apd-save-btn"
                  onClick={handleSaveBudget}
                  disabled={isUpdating}
                  title="Save"
                >
                  <FaCheck /> Save
                </button>
                <button
                  className="apd-cancel-btn"
                  onClick={() => setIsEditingBudget(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="apd-info-budget">
              ${project?.budget?.toLocaleString() || "TBD"}
            </p>
          )}
        </div>
      </div>

      {/* ========== MODALS ========== */}

      {/* Client Info Modal */}
      {showClientInfoModal && project?.client?.id && (
        <ClientInfoModal
          clientId={project.client.id}
          isOpen={showClientInfoModal}
          onClose={() => setShowClientInfoModal(false)}
        />
      )}

      {/* Attach Client Modal */}
      <AttachClientModal
        isOpen={showAttachClientModal}
        onClose={() => setShowAttachClientModal(false)}
        projectId={projectId}
        onAttach={handleClientAttached}
      />
    </div>
  );
};

export default ProjectInfoHeader;
